import { Row, Col, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-light-svg-icons";
import { faSortDown, faSortUp } from "@fortawesome/pro-solid-svg-icons";
import { useState } from "react";
import CurrencyFormat from "react-currency-format";
import DataTable from "@components/DataTable/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useFlags from "@common/hooks/useFlags";
import { useV2GetAccountQuery } from "@generated";
import { usePendingApprovals } from "./api";
import { PendingApproval, TimeWaitingDirection } from "./types";
import { useStyles } from "./styles";

const DEFAULT_PAGE_SIZE = 25;

const PendingApprovals = (): JSX.Element => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { releaseReactNav } = useFlags();
  const { data: account } = useV2GetAccountQuery({});
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [timeWaitingDirection, setTimeWaitingDirection] =
    useState<TimeWaitingDirection>("desc");
  const { data, pageMeta, isLoading } = usePendingApprovals({
    pageNumber,
    pageSize,
    timeWaitingDirection,
  });

  const toggleTimeWaiting = (): void => {
    setTimeWaitingDirection((d) => (d === "desc" ? "asc" : "desc"));
    setPageNumber(1);
  };

  const currencyUnit =
    account?.data?.attributes?.["field-labels"]?.["currency_unit"] ?? "$";

  const appHost = process.env.REACT_APP_DOORKEEPER_APP_URL;

  const goToProjectApprovals = (projectId: string): void => {
    const path = `/projects/${projectId}/project_approvals`;
    if (releaseReactNav) {
      navigate(path);
    } else {
      window.location.assign(`${appHost}${path}`);
    }
  };

  const columns: ColumnDef<PendingApproval>[] = [
    {
      header: "Project Name",
      cell: (props) => (
        <span
          className={styles.projectLink}
          onClick={() => goToProjectApprovals(props.row.original.projectId)}
        >
          {props.row.original.projectName}
        </span>
      ),
    },
    {
      header: "Customer",
      cell: (props) => props.row.original.clientName,
    },
    {
      header: "Revenue",
      cell: (props) => (
        <span className={styles.numericCell}>
          <CurrencyFormat
            displayType="text"
            prefix={currencyUnit}
            isNumericString
            thousandSeparator
            value={props.row.original.contractRevenue.toFixed(2)}
          />
        </span>
      ),
    },
    {
      header: "Margin",
      cell: (props) => (
        <span className={styles.numericCell}>
          {new Intl.NumberFormat("en-US", {
            style: "percent",
            minimumFractionDigits: 2,
          }).format(props.row.original.contractMargin)}
        </span>
      ),
    },
    {
      id: "timeWaiting",
      header: () => (
        <button
          type="button"
          className={styles.sortHeader}
          onClick={toggleTimeWaiting}
          aria-label={`Time Waiting, sorted ${
            timeWaitingDirection === "desc" ? "longest" : "shortest"
          } first`}
        >
          Time Waiting
          <FontAwesomeIcon
            icon={timeWaitingDirection === "desc" ? faSortDown : faSortUp}
          />
        </button>
      ),
      cell: (props) => props.row.original.timeWaiting,
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Row>
        <Col>
          <h1 className={styles.title}>Projects for My Approval</h1>
        </Col>
        <hr className={styles.line} />
      </Row>
      <Row>
        <Col>
          <Card className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <FontAwesomeIcon
                  className={styles.cardIcon}
                  icon={faCircleCheck}
                />
                <h2>Pending Approvals</h2>
              </div>
            </div>
            <hr className={styles.line} />
            {isLoading ? (
              <ScopeStackSpinner />
            ) : pageMeta.recordCount === 0 ? (
              <div className={styles.emptyState}>
                <h3>You're all caught up!</h3>
                <p>No projects are currently waiting on your approval.</p>
              </div>
            ) : (
              <DataTable
                data={data}
                columns={columns}
                striped
                paginationEnabled
                pageSizeEnabled
                totalPages={pageMeta.pageCount}
                totalRows={pageMeta.recordCount}
                currentPage={pageNumber}
                setCurrentPage={setPageNumber}
                pageSize={pageSize}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPageNumber(1);
                }}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PendingApprovals;
